<template>
    <div class="d-flex">
        <div style="width: 100%">
            <FromSelection
                :email-integration-id="emailIntegrationId"
                :is-cc-active="isCcActive"
                :is-bcc-active="isBccActive"
                :account-id="accountId"
                :client-id="clientId"
                :include-private-from-emails="includePrivateFromEmails"
                @update-email-integration-id="$emit('update-email-integration-id', $event)"
            />
            <RecipientSelection
                type="to"
                :contact-tabs="contactTabs"
                :errors="errors"
                :recipients="toRecipients"
                :only-automations="onlyAutomations"
                :account-id="accountId"
                :is-cc-active="isCcActive"
                :is-bcc-active="isBccActive"
                @toggleCc="$emit('toggleCc')"
                @toggleBcc="$emit('toggleBcc')"
                @updateRecipients="$emit('updateToRecipients', $event)"
            />
            <RecipientSelection
                v-if="isCcActive"
                type="cc"
                :errors="errors"
                :contact-tabs="contactTabs"
                :recipients="ccRecipients"
                :only-automations="onlyAutomations"
                :account-id="accountId"
                @updateRecipients="$emit('updateCcRecipients', $event)"
            />
            <RecipientSelection
                v-if="isBccActive"
                type="bcc"
                :errors="errors"
                :contact-tabs="contactTabs"
                :recipients="bccRecipients"
                :only-automations="onlyAutomations"
                :account-id="accountId"
                @updateRecipients="$emit('updateBccRecipients', $event)"
            />
            <v-text-field
                ref="subjectTextField"
                :value="subject"
                hide-details
                background-color="white"
                :error-messages="errors.subject"
                :readonly="Boolean(templateId)"
                test-id="task-add-email-subject"
                style="border-radius: 0; border-top: 0; border-bottom: 0; font-size: 14px"
                class="mb-0 pt-0 text-field-no-border"
                @input="updateSubject($event)"
                @click="emitFocusInfo('subject')"
                @keyup="emitFocusInfo('subject')"
            >
                <template v-slot:prepend-inner>
                    <div class="subtitle-2 ml-2 mr-1" style="margin-top: 2px"
                         :class="errors.subject ? 'error--text' : 'n3--text'">{{ $t("Subject") }}
                    </div>
                </template>
            </v-text-field>
            <div style="position:relative;">
                <RichTextEditor
                    ref="richTextEditor"
                    :toolbar="toolbarId"
                    :disabled="Boolean(templateId)"
                    :error-message="errors.content"
                    :content="content"
                    @input="updateContent"
                    @focus="emitFocusInfo('content')"
                />
                <div
                    v-if="templateId"
                    class="d-flex align-center"
                    style="position:absolute; right: 8px; bottom: 8px;"
                >
                    <div class="body-2 n3--text">{{ $t("Edit email") }}</div>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <div v-on="on">
                                <icon-button @click="clearTemplate">
                                    mdi-pencil
                                </icon-button>
                            </div>
                        </template>
                        <span>{{ selectedTemplateTooltip }}</span>
                    </v-tooltip>
                </div>
            </div>
            <div v-if="files.length" class="mt-2">
                <AttachmentsList
                    :files="files"
                    color="n2"
                    @download="download($event)"
                    @delete_file="$emit('deleteFile', $event)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import RecipientSelection from "./RecipientSelection";
import AttachmentsList from "../../../components/AttachmentsList";
import { downloadFile } from "@/helpers/helpers";
import { $t } from "@/i18n";
import { ref, computed } from "@vue/composition-api";
import EmailAutomationModule from "../../../store/modules/email_automation";
import FromSelection from "./FromSelection";

export default {
    name: "TaskAddEmailControlEmail",
    components: {
        RichTextEditor,
        RecipientSelection,
        AttachmentsList,
        FromSelection
    },
    props: {
        isCcActive: {
            type: Boolean,
            required: true
        },
        isBccActive: {
            type: Boolean,
            required: true
        },
        toRecipients: {
            type: Array,
            required: true
        },
        ccRecipients: {
            type: Array,
            required: true
        },
        bccRecipients: {
            type: Array,
            required: true
        },
        subject: {
            type: String,
            required: true
        },
        content: {
            type: String,
            required: true
        },
        files: {
            type: Array,
            required: true
        },
        contactTabs: {
            type: Array,
            required: true
        },
        errors: {
            type: Object,
            default: null
        },
        templateId: {
            type: Number,
            default: null
        },
        selectedLocaleCode: {
            type: String,
            default: null
        },
        onlyAutomations: {
            type: Boolean,
            required: true
        },
        accountId: {
            type: Number,
            required: true
        },
        emailIntegrationId: {
            type: Number,
            default: null
        },
        clientId: {
            type: Number,
            default: null
        },
        includePrivateFromEmails: {
            type: Boolean,
            default: false
        },
        toolbarId: {
            type: String,
            required: true
        }
    },
    setup(props, ctx) {
        const { emailAutomationTemplates } = EmailAutomationModule()
        const subjectTextField = ref(null);

        const download = (file) => {
            downloadFile('email_task/attachment', file)
        }

        const clearTemplate = () => {
            ctx.emit('clearTemplate')
        }

        const updateContent = (e) => {
            if (!props.templateId) {
                ctx.emit('updateContent', e)
            }
        }

        const selectedTemplateTooltip = computed(() => {
            const template = emailAutomationTemplates.value.find(x => x.id === props.templateId)
            if (template) {
                return `${$t('Template')}: ${template.template_name} (${props.selectedLocaleCode.substring(0, 2).toUpperCase()})`
            }
        })

        const updateSubject = (event) => {
            emitFocusInfo("subject")
            ctx.emit('updateSubject', event)
        }

        const emitFocusInfo = (fieldName) => {
            let cursorIndex = null
            if (fieldName === "subject") {
                const subjectField = subjectTextField.value.$refs.input;
                cursorIndex = subjectField.selectionStart;

            }
            ctx.emit('focusInfo', {fieldName: fieldName, cursorIndex: cursorIndex})
        }

        return {
            subjectTextField,
            download,
            clearTemplate,
            updateContent,
            selectedTemplateTooltip,
            emitFocusInfo,
            updateSubject,
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep .quill-editor .ql-container .ql-editor {
    height: 172px !important;
}

.text-field-no-border {
    ::v-deep .v-input__slot {
        &:after, &:before {
            display: none !important;
        }
    }

    ::v-deep input {
        font-weight: 600 !important;
    }

    &.v-input--is-readonly {
        ::v-deep input {
            color: var(--v-g4-base) !important;
        }
    }
}

::v-deep .v-select.v-select--chips.v-input--dense {
    .v-select__selections {
        min-height: 36px;
    }
}

</style>